import React from "react";
import styled from "styled-components";
import { motion } from "framer-motion";

import type { AboutSectionCopy } from "../aboutContent";

/**
 * 会社概要の全画面セクション（CONCEPT / FEATURE）。
 * /careers の CareersMessage（白地）・CareersConcept（青地）と同じ
 * フォント・行間・出現アニメーションに揃える。
 *
 * - theme="light": 背景 var(--soremo-background) / 文字 #000（CareersMessage 相当）
 * - theme="blue" : 背景 #009ace / 文字 #fff（CareersConcept 相当）
 */
export interface AboutFullpageProps {
  copy: AboutSectionCopy;
  theme?: "light" | "blue";
}

const Page = styled.section<{ $theme: "light" | "blue" }>`
  min-height: 100dvh;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 64px 24px;
  color: ${({ $theme }) => ($theme === "blue" ? "#fff" : "#000")};
  background-color: ${({ $theme }) =>
    $theme === "blue" ? "#009ace" : "var(--soremo-background)"};

  /* スクロールスナップ（親の AboutScrollArea が scroll-snap-type を持つ） */
  scroll-snap-align: start;
`;

/* careers は 680px だが、CONCEPT のコピー（見出し 18 文字 / 本文 47 文字の指定改行）が
   PC で折れずに収まるよう 800px にしている（h2 の字間 2.5px 込み） */
const Inner = styled(motion.div)`
  max-width: 800px;
  width: 100%;
  display: flex;
  flex-direction: column;
`;

/* セクション種別（CONCEPT / FEATURE）と英語見出し。careers のヒーローと同じ din-2014 */
const Eyebrow = styled(motion.p).attrs({
  className: "heading-spacing-13",
})`
  opacity: 0.7;
  margin-bottom: 16px;
`;

const TitleEn = styled(motion.p)`
  font-family: "din-2014-rounded-variable", sans-serif;
  font-variation-settings: "wght" 200;
  font-size: clamp(2rem, 1.4rem + 3vw, 4rem);
  line-height: 110%;
  letter-spacing: 0.05em;
  margin-bottom: 24px;
`;

/* 「動けば、世界は変わる。」と同じ見出しスタイル */
const TitleJa = styled(motion.h2)`
  font-family: "A+mfCv-AXISラウンド 50 L StdN", "M PLUS 1p", sans-serif;
  font-size: clamp(1.313rem, 0.849rem + 2.32vw, 2.5rem);
  line-height: 187.5%;
  margin-bottom: 48px;
  /* コピーの改行位置（"\n"）を活かす */
  white-space: pre-line;

  @media (max-width: 640px) {
    margin-bottom: 32px;
  }
`;

const Paragraph = styled(motion.p)`
  font-family: "A+mfCv-AXISラウンド 50 L StdN", "M PLUS 1p", sans-serif;
  font-size: clamp(0.875rem, 0.8rem + 0.375vw, 1rem);
  line-height: 200%;
  text-align: justify;
  word-break: normal;
  /* コピーの改行位置（"\n"）は PC のみ活かす。狭い画面では不自然に折れるので無視 */
  white-space: pre-line;

  @media (max-width: 640px) {
    text-align: left;
    white-space: normal;
  }

  &:not(:last-child) {
    margin-bottom: 2.5em;
  }
`;

/* FEATURE 項目の本文。項目間は ItemRow の上余白で取るので段落余白は持たない */
const ItemBody = styled(Paragraph)`
  &:not(:last-child) {
    margin-bottom: 0;
  }
`;

/* FEATURE の 3 項目。CareersConcept の「原則」行（丸番号 + 見出し + 本文）を踏襲 */
const ItemRow = styled(motion.div).attrs({
  className: "u-row u-items-start u-gap16 u-mt24 u-mb8",
})``;

const NumberCounter = styled.span.attrs({
  className: "heading-spacing-18",
})`
  line-height: 100%;
  letter-spacing: 0;
  user-select: none;
  flex-shrink: 0;

  width: clamp(2.5rem, 2.11rem + 1.95vw, 3.5rem);
  height: clamp(2.5rem, 2.11rem + 1.95vw, 3.5rem);

  border-radius: 50%;
  border: 1px solid currentColor;

  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
`;

const ItemTitle = styled.h4`
  font-family: "din-2014-rounded-variable", sans-serif;
  font-variation-settings: "wght" 300;
  font-size: clamp(1.25rem, 1rem + 1vw, 1.75rem);
  line-height: 100%;
  letter-spacing: 0.05em;
  padding-top: 12px;
`;

/* 項目の一言（「作品と、音をつくる人をつなぐ。」）。本文より一段強く */
const ItemLead = styled(motion.p)`
  font-family: "A+mfCv-AXISラウンド 50 R StdN", "M PLUS 1p", sans-serif;
  font-size: clamp(1rem, 0.9rem + 0.5vw, 1.125rem);
  line-height: 175%;
  margin-bottom: 4px;
`;

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.2, delayChildren: 0.3 },
  },
};

const staggerItem = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
};

const AboutFullpage: React.FC<AboutFullpageProps> = ({ copy, theme = "light" }) => {
  return (
    <Page $theme={theme}>
      <Inner
        initial="hidden"
        whileInView="visible"
        viewport={{ once: false, amount: 0.3 }}
        variants={staggerContainer}
      >
        <Eyebrow variants={staggerItem}>{copy.label}</Eyebrow>
        <TitleEn variants={staggerItem}>{copy.titleEn}</TitleEn>
        <TitleJa variants={staggerItem}>{copy.titleJa}</TitleJa>

        {copy.paragraphs.map((text) => (
          <Paragraph key={text} variants={staggerItem}>
            {text}
          </Paragraph>
        ))}

        {copy.items?.map((item) => (
          <React.Fragment key={item.index}>
            <ItemRow variants={staggerItem}>
              <NumberCounter>{item.index}</NumberCounter>
              <ItemTitle>{item.title}</ItemTitle>
            </ItemRow>
            {item.lead ? <ItemLead variants={staggerItem}>{item.lead}</ItemLead> : null}
            <ItemBody variants={staggerItem}>{item.body}</ItemBody>
          </React.Fragment>
        ))}
      </Inner>
    </Page>
  );
};

export default AboutFullpage;
